import { format } from 'date-fns'
import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { z } from 'zod/v3'
import type { AuditorRiskDetails } from '@deepguide-ai/types'
import { Input } from '@deepguide-ai/ui/input'
import { ChevronRight } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@deepguide-ai/ui/table'
import { TableEmptyState } from '@deepguide-ai/ui/table-empty-state'
import { TableSkeleton } from '@deepguide-ai/ui/table-skeleton'
import ScreenataPageHeader from '@/components/compliance/ScreenataPageHeader'
import { PageHeaderBreadcrumb } from '@/components/layout/PageHeaderActions'
import {
  DetailPanelLayout,
  DetailPanelMain,
  DetailPanelSkeleton,
  DetailPanelTitle,
  DetailSection,
  DetailStatusGrid,
  DetailStatusItem,
} from '@/components/compliance/detail-primitives'
import { TablePaginationFooter } from '@/components/ui/TablePaginationFooter'
import { DEFAULT_PAGE_SIZE, paginationSearchSchema } from '@/lib/pagination'
import { trpc } from '@/lib/trpc'
import { RiskTierBadge } from '@/pages/vendors/BadgeComponents'
import { RISK_STATUS_LABELS, StatusBadge, TreatmentLabel } from '@/pages/risk/RiskBadges'
import { MarkdownDetailContent } from '@/pages/risk/RiskDetailPanel'

export const auditorRiskSearchSchema = paginationSearchSchema.extend({
  search: z.string().optional().catch(undefined),
  riskRating: z
    .enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'])
    .optional()
    .catch(undefined),
  status: z
    .enum(['OPEN', 'IN_PROGRESS', 'MONITORING', 'CLOSED'])
    .optional()
    .catch(undefined),
})

const LIST_ROUTE =
  '/workspaces/$workspaceId/auditor-center/$auditCycleId/risks' as const
const DETAIL_ROUTE =
  '/workspaces/$workspaceId/auditor-center/$auditCycleId/risks/$riskId' as const

const date = (value: Date | null) => (value ? format(value, 'PP') : '—')
const level = (value: string | null) =>
  value ? value.charAt(0) + value.slice(1).toLowerCase() : '—'

export function AuditorRisksPage() {
  const { workspaceId, auditCycleId } = useParams({ strict: false })
  const filters = auditorRiskSearchSchema.parse(useSearch({ strict: false }))
  const navigate = useNavigate()
  const page = filters.page ?? 1
  const pageSize = filters.pageSize ?? DEFAULT_PAGE_SIZE
  const risks = useQuery(
    trpc.auditor.getRisks.queryOptions({
      auditCycleId: auditCycleId!,
      search: filters.search,
      riskRating: filters.riskRating,
      status: filters.status,
      page,
      pageSize,
    })
  )
  const params = { workspaceId: workspaceId!, auditCycleId: auditCycleId! }
  const update = (next: Partial<typeof filters>) =>
    navigate({
      to: LIST_ROUTE,
      params,
      search: { ...filters, page: 1, ...next },
      replace: true,
    })

  return (
    <div className='space-y-4 p-6'>
      <ScreenataPageHeader
        title='Risks'
        titleClassName='text-foreground'
        description='The risk register: rating, treatment, owner and residual risk for each risk. Read-only.'
      />
      <div className='flex flex-wrap items-center gap-3'>
        <Select
          value={filters.riskRating ?? 'all'}
          onValueChange={(value) =>
            void update({
              riskRating:
                value === 'all'
                  ? undefined
                  : (value as typeof filters.riskRating),
            })
          }
        >
          <SelectTrigger className='w-40' aria-label='Rating'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All ratings</SelectItem>
            <SelectItem value='CRITICAL'>Critical</SelectItem>
            <SelectItem value='HIGH'>High</SelectItem>
            <SelectItem value='MEDIUM'>Medium</SelectItem>
            <SelectItem value='LOW'>Low</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={filters.status ?? 'all'}
          onValueChange={(value) =>
            void update({
              status:
                value === 'all' ? undefined : (value as typeof filters.status),
            })
          }
        >
          <SelectTrigger className='w-40' aria-label='Status'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All statuses</SelectItem>
            <SelectItem value='OPEN'>{RISK_STATUS_LABELS.OPEN}</SelectItem>
            <SelectItem value='IN_PROGRESS'>{RISK_STATUS_LABELS.IN_PROGRESS}</SelectItem>
            <SelectItem value='MONITORING'>{RISK_STATUS_LABELS.MONITORING}</SelectItem>
            <SelectItem value='CLOSED'>{RISK_STATUS_LABELS.CLOSED}</SelectItem>
          </SelectContent>
        </Select>
        <Input
          aria-label='Search risks'
          placeholder='Search title, statement or category…'
          value={filters.search ?? ''}
          onChange={(event) =>
            void update({ search: event.target.value || undefined })
          }
          className='w-full sm:ml-auto sm:max-w-xs'
        />
      </div>
      {risks.isPending && (
        <TableSkeleton
          columns={8}
          rows={5}
          className='bg-card rounded-md'
          cellClassName='px-3 py-2.5'
        />
      )}
      {risks.isError && <p role='alert'>{risks.error.message}</p>}
      {risks.data &&
        (risks.data.items.length === 0 ? (
          <TableEmptyState
            message='No risks match these filters.'
            className='bg-card rounded-md'
          />
        ) : (
          <div className='bg-card overflow-x-auto rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Risk</TableHead>
                  <TableHead>Likelihood × Impact</TableHead>
                  <TableHead>Inherent</TableHead>
                  <TableHead>Treatment</TableHead>
                  <TableHead>Residual</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Next review</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {risks.data.items.map((risk) => (
                  <TableRow key={risk.id}>
                    <TableCell className='font-mono text-xs whitespace-nowrap'>
                      <Link
                        to={DETAIL_ROUTE}
                        params={{ ...params, riskId: risk.id }}
                        search={filters}
                        className='text-cobalt-strong hover:underline'
                      >
                        {risk.displayId}
                      </Link>
                    </TableCell>
                    <TableCell className='max-w-md'>
                      <Link
                        to={DETAIL_ROUTE}
                        params={{ ...params, riskId: risk.id }}
                        search={filters}
                        className='font-medium hover:underline'
                      >
                        {risk.title}
                      </Link>
                      <div className='text-muted-foreground text-xs'>
                        {risk.category}
                      </div>
                    </TableCell>
                    <TableCell className='text-xs whitespace-nowrap'>
                      {level(risk.likelihood)} × {level(risk.impact)}
                    </TableCell>
                    <TableCell>
                      <RiskTierBadge tier={risk.riskRating} />
                    </TableCell>
                    <TableCell>
                      <TreatmentLabel treatment={risk.treatment} />
                    </TableCell>
                    <TableCell>
                      {risk.residualRisk ? (
                        <RiskTierBadge tier={risk.residualRisk} />
                      ) : (
                        <span className='text-muted-foreground text-xs'>
                          Not assessed
                        </span>
                      )}
                    </TableCell>
                    <TableCell className='text-xs'>{risk.owner}</TableCell>
                    <TableCell>
                      <StatusBadge status={risk.status} />
                    </TableCell>
                    <TableCell className='text-xs whitespace-nowrap'>
                      {date(risk.reviewDate)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePaginationFooter
              page={page}
              pageSize={pageSize}
              totalItems={risks.data.total}
              onPageChange={(value) => void update({ page: value })}
              onPageSizeChange={(value) => void update({ pageSize: value })}
            />
          </div>
        ))}
      <p className='text-text-subtle text-[11px] leading-normal'>
        Current register, not an audit-period snapshot. The published risk
        assessment is the evidence on the Risk Assessment tests.
      </p>
    </div>
  )
}

export function AuditorRiskDetailsPage() {
  const { workspaceId, auditCycleId, riskId } = useParams({ strict: false })
  const filters = auditorRiskSearchSchema.parse(useSearch({ strict: false }))
  const details = useQuery(
    trpc.auditor.getRiskDetails.queryOptions({
      auditCycleId: auditCycleId!,
      riskId: riskId!,
    })
  )
  const breadcrumb = (
    <PageHeaderBreadcrumb>
      <div className='flex min-w-0 items-center gap-1.5 text-[13px]'>
        <ChevronRight className='size-3 shrink-0 text-text-placeholder' />
        <Link
          to={LIST_ROUTE}
          params={{ workspaceId: workspaceId!, auditCycleId: auditCycleId! }}
          search={filters}
          className='text-text-body transition-colors hover:text-text-secondary'
        >
          Risks
        </Link>
        <ChevronRight className='size-3 shrink-0 text-text-placeholder' />
        <span className='max-w-[240px] truncate font-semibold text-foreground'>
          {details.data?.title ?? 'Risk'}
        </span>
      </div>
    </PageHeaderBreadcrumb>
  )
  if (details.isPending) return <>{breadcrumb}<DetailPanelSkeleton /></>
  return (
    <>
      {breadcrumb}
      <DetailPanelLayout className='h-full max-h-none'>
        <DetailPanelMain
          className='bg-card'
          contentClassName='space-y-6 text-sm text-text-body'
        >
          {details.isError && <p role='alert'>{details.error.message}</p>}
          {details.data && (
            <RiskDetails
              risk={details.data}
              workspaceId={workspaceId!}
              auditCycleId={auditCycleId!}
            />
          )}
        </DetailPanelMain>
      </DetailPanelLayout>
    </>
  )
}

function RiskDetails({
  risk,
  workspaceId,
  auditCycleId,
}: {
  risk: AuditorRiskDetails
  workspaceId: string
  auditCycleId: string
}) {
  const acceptance = risk.acceptance
  return (
    <>
      <header>
        <DetailPanelTitle code={risk.displayId} title={risk.title}>
          <p className='text-muted-foreground mt-2 text-sm'>{risk.category}</p>
        </DetailPanelTitle>
        <DetailStatusGrid className='mt-6 max-w-none sm:grid-cols-3 lg:grid-cols-5'>
          <DetailStatusItem label='Inherent risk'>
            <RiskTierBadge tier={risk.riskRating} />
          </DetailStatusItem>
          <DetailStatusItem label='Treatment'>
            <TreatmentLabel treatment={risk.treatment} />
          </DetailStatusItem>
          <DetailStatusItem label='Residual risk'>
            {risk.residualRisk ? (
              <RiskTierBadge tier={risk.residualRisk} />
            ) : (
              'Not assessed'
            )}
          </DetailStatusItem>
          <DetailStatusItem label='Status'>
            <StatusBadge status={risk.status} />
          </DetailStatusItem>
          <DetailStatusItem label='Owner'>{risk.owner}</DetailStatusItem>
        </DetailStatusGrid>
        <p className='text-muted-foreground mt-5 text-xs'>
          Read only · Current register, not an audit-period snapshot.
        </p>
      </header>

      <DetailSection title='Risk'>
        <MarkdownDetailContent content={risk.riskStatement} />
        <DetailStatusGrid className='mt-4 max-w-none sm:grid-cols-3'>
          <DetailStatusItem label='Affected system'>
            {risk.affectedSystem ?? '—'}
          </DetailStatusItem>
          <DetailStatusItem label='Identified'>
            {date(risk.createdAt)}
          </DetailStatusItem>
          <DetailStatusItem label='Source'>
            {level(risk.sourceType).replace(/_/g, ' ')}
            {risk.provider !== 'manual' ? ` · ${risk.provider}` : ''}
          </DetailStatusItem>
        </DetailStatusGrid>
      </DetailSection>

      <DetailSection title='Assessment'>
        <DetailStatusGrid className='max-w-none sm:grid-cols-2'>
          <DetailStatusItem label='Inherent likelihood × impact'>
            {level(risk.likelihood)} × {level(risk.impact)}
          </DetailStatusItem>
          <DetailStatusItem label='Residual likelihood × impact'>
            {risk.residualLikelihood && risk.residualImpact
              ? `${level(risk.residualLikelihood)} × ${level(risk.residualImpact)}`
              : 'Not assessed'}
          </DetailStatusItem>
          <DetailStatusItem label='Target date'>
            {date(risk.targetDate)}
          </DetailStatusItem>
          <DetailStatusItem label='Next review'>
            {date(risk.reviewDate)}
          </DetailStatusItem>
        </DetailStatusGrid>
        {risk.cveIds.length > 0 && (
          <p className='text-muted-foreground mt-3 text-xs'>
            {risk.cveIds.join(', ')}
            {risk.packageName ? ` · ${risk.packageName}` : ''}
            {risk.slaDueAt ? ` · remediation due ${date(risk.slaDueAt)}` : ''}
          </p>
        )}
      </DetailSection>

      <DetailSection title='Treatment plan'>
        {risk.mitigationPlan ? (
          <MarkdownDetailContent content={risk.mitigationPlan} />
        ) : (
          <p>No treatment plan recorded.</p>
        )}
      </DetailSection>

      {acceptance && (
        <DetailSection title={acceptance.status === 'PENDING' ? 'Exception requested' : 'Risk acceptance'}>
          <MarkdownDetailContent content={acceptance.justification} />
          <DetailStatusGrid className='mt-4 max-w-none sm:grid-cols-4'>
            <DetailStatusItem label={acceptance.status === 'PENDING' ? 'Assigned approver' : 'Approved by'}>
              {acceptance.approverName ?? '—'}
            </DetailStatusItem>
            <DetailStatusItem label='Approved on'>
              {date(acceptance.approvedAt)}
            </DetailStatusItem>
            <DetailStatusItem label='Expires'>
              {date(acceptance.expiresAt)}
            </DetailStatusItem>
            <DetailStatusItem label='Status'>
              {level(acceptance.status)}
              {acceptance.revokedAt
                ? ` on ${date(acceptance.revokedAt)}${acceptance.revokedReason ? `: ${acceptance.revokedReason}` : ''}`
                : ''}
            </DetailStatusItem>
          </DetailStatusGrid>
        </DetailSection>
      )}

      <DetailSection title={`Tests (${risk.linkedTests.length})`}>
        {risk.linkedTests.length ? (
          <ul className='space-y-1.5'>
            {risk.linkedTests.map((test) => (
              <li key={test.cesId}>
                <Link
                  to='/workspaces/$workspaceId/auditor-center/$auditCycleId/controls'
                  params={{ workspaceId, auditCycleId }}
                  search={{ test: test.cesId }}
                  className='hover:underline'
                >
                  {test.displayId && (
                    <span className='text-muted-foreground mr-2 font-mono text-xs'>
                      {test.displayId}
                    </span>
                  )}
                  {test.title}
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className='text-muted-foreground'>No tests linked.</p>
        )}
      </DetailSection>

      <DetailSection title={`Affected assets (${risk.linkedAssets.length})`}>
        {risk.linkedAssets.length ? (
          <ul className='space-y-1.5'>
            {risk.linkedAssets.map((asset, index) => (
              <li key={`${asset.displayId}-${index}`}>
                {asset.displayId && (
                  <span className='text-muted-foreground mr-2 font-mono text-xs'>
                    {asset.displayId}
                  </span>
                )}
                {asset.name}
                {asset.criticality && (
                  <span className='text-muted-foreground text-xs'>
                    {' '}
                    · {level(asset.criticality)} criticality
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className='text-muted-foreground'>No assets linked.</p>
        )}
      </DetailSection>

      <DetailSection title={`Vendors (${risk.linkedVendors.length})`}>
        {risk.linkedVendors.length ? (
          <ul className='space-y-1.5'>
            {risk.linkedVendors.map((vendor) => (
              <li key={vendor.vendorId}>
                <Link
                  to='/workspaces/$workspaceId/auditor-center/$auditCycleId/vendors/$vendorId'
                  params={{ workspaceId, auditCycleId, vendorId: vendor.vendorId }}
                  search={{ tab: 'overview' }}
                  className='hover:underline'
                >
                  {vendor.displayId && (
                    <span className='text-muted-foreground mr-2 font-mono text-xs'>
                      {vendor.displayId}
                    </span>
                  )}
                  {vendor.name}
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className='text-muted-foreground'>No vendors linked.</p>
        )}
      </DetailSection>
    </>
  )
}
